import { Layer, Source, useMap } from 'react-map-gl/maplibre'
import type { MapMouseEvent } from 'maplibre-gl'
import type { FeatureCollection } from 'geojson'
import { useEffect, useRef } from 'react'
import style from './AreaTable.module.css'
import { Pt } from './polygonHit'

// Draw-a-shape tool. Use it as a child of <LocationMap>.
// Click to add points, double-click (or "Finish") to close the shape.

const LAYER_IDS = ['draw-fill', 'draw-line', 'draw-points']
const DRAW_COLOR = '#22d3ee'

interface Props {
    drawing: boolean
    points: Pt[]
    hasShape: boolean                      // a finished shape is on the map
    onStart: () => void
    onAddPoint: (p: Pt) => void
    onFinish: (zoom: number) => void
    onCancel: () => void
}

const PolygonSelectTool = ({ drawing, points, hasShape, onStart, onAddPoint, onFinish, onCancel }: Props) => {
    const { current: map } = useMap()

    // keep the latest callbacks without re-binding map events every render
    const cb = useRef({ onAddPoint, onFinish })
    cb.current = { onAddPoint, onFinish }

    // map clicks while drawing
    useEffect(() => {
        if (!map || !drawing) return
        const m = map.getMap()

        const click = (e: MapMouseEvent) => cb.current.onAddPoint([e.lngLat.lng, e.lngLat.lat])
        const dblclick = (e: MapMouseEvent) => {
            e.preventDefault()
            cb.current.onFinish(m.getZoom())
        }

        m.doubleClickZoom.disable()
        m.on('click', click)
        m.on('dblclick', dblclick)
        return () => {
            m.off('click', click)
            m.off('dblclick', dblclick)
            m.doubleClickZoom.enable()
        }
    }, [map, drawing])

    // boundary levels are added lazily on zoom, so lift the drawing back on top
    useEffect(() => {
        if (!map) return
        const m = map.getMap()
        const toTop = () => setTimeout(() => LAYER_IDS.forEach((id) => m.getLayer(id) && m.moveLayer(id)), 0)
        toTop()
        m.on('zoomend', toTop)
        return () => { m.off('zoomend', toTop) }
    }, [map, points.length])

    const ring = points.length >= 3 ? [...points, points[0]] : points
    const shape: FeatureCollection = {
        type: 'FeatureCollection',
        features: [
            ...(points.length >= 3
                ? [{ type: 'Feature' as const, properties: {}, geometry: { type: 'Polygon' as const, coordinates: [ring] } }]
                : []),
            ...(points.length >= 2
                ? [{ type: 'Feature' as const, properties: {}, geometry: { type: 'LineString' as const, coordinates: ring } }]
                : []),
            ...points.map((p) => ({ type: 'Feature' as const, properties: {}, geometry: { type: 'Point' as const, coordinates: p } })),
        ],
    }

    return (
        <>
            {points.length > 0 && (
                <Source id="draw-shape" type="geojson" data={shape}>
                    <Layer id="draw-fill" type="fill" filter={['==', ['geometry-type'], 'Polygon']}
                           paint={{ 'fill-color': DRAW_COLOR, 'fill-opacity': 0.18 }} />
                    <Layer id="draw-line" type="line" filter={['==', ['geometry-type'], 'LineString']}
                           paint={{ 'line-color': DRAW_COLOR, 'line-width': 2, 'line-dasharray': [2, 1] }} />
                    <Layer id="draw-points" type="circle" filter={['==', ['geometry-type'], 'Point']}
                           paint={{ 'circle-radius': 4, 'circle-color': '#fff', 'circle-stroke-color': DRAW_COLOR, 'circle-stroke-width': 2 }} />
                </Source>
            )}

            <div className={style.DrawBar}>
                {!drawing && (
                    <button className={style.DrawBtn} onClick={onStart}>
                        {hasShape ? 'Draw again' : 'Draw area'}
                    </button>
                )}
                {drawing && (
                    <>
                        <span className={style.DrawHint}>Click to add points, double-click to finish</span>
                        <button
                            className={`${style.DrawBtn} ${style.DrawBtnPrimary}`}
                            disabled={points.length < 3}
                            onClick={() => map && onFinish(map.getZoom())}
                        >
                            Finish
                        </button>
                    </>
                )}
                {(drawing || hasShape) && (
                    <button className={style.DrawBtn} onClick={onCancel}>Clear</button>
                )}
            </div>
        </>
    )
}

export default PolygonSelectTool
